import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaSchool, FaEye } from "react-icons/fa";
import styles from "./List.module.css";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useSelector } from "react-redux";

const ClassList = () => {
  const { translations } = useSelector((state) => state.language);

  const navigate = useNavigate();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const academic_year = clientData?.academic_year || "";
  const school_id = clientData?.school_id || "";
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Fetch class list on component mount and when dependencies change
  useEffect(() => {
    if (school_id) {
      fetchClassList();
    }
  }, [school_id]);

  // API call to fetch class list
  const fetchClassList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/school/classList`, {
        id: school_id,
        academic_year: academic_year,
      });

      if (response?.data?.success) {
        const apiData = response.data.data || [];

        // Transform API data to match table structure
        const transformedData = apiData
          .filter((item) => item.domain_id !== "CLASS")
          .map((item) => ({
            id: item.id,
            class_id: item.class_id,
            class: item.domain_id,
            total_boys: item.total_boys || 0,
            total_girls: item.total_girls || 0,
            total_students: item.total_students || 0,
            no_of_section: item.no_of_section || 0,
          }));

        setData(transformedData);
        setRowCount(transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching class list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;
    const searchLower = apiSearch.toLowerCase();
    return data.filter(
      (item) =>
        item.class?.toLowerCase().includes(searchLower) ||
        item.total_boys?.toString().includes(searchLower) ||
        item.total_girls?.toString().includes(searchLower) ||
        item.total_students?.toString().includes(searchLower),
    );
  }, [apiSearch, data]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  // Handle view action - navigate to ClassSectionList with class data
  const handleView = (row) => {
    navigate("/user/classSectionList", {
      state: {
        classId: row.class_id,
        className: row.class,
      },
    });
  };

  // Table columns - Class, No. of Male, No. of Female, Total Students, Actions
  const columns = useMemo(
    () => [
      {
        accessorKey: "class",
        header: translations.labels.class || "Class",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "total_boys",
        header: translations.labels.noOfMale || "No. of Male",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "total_girls",
        header: translations.labels.noOfFemale || "No. of Female",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "total_students",
        header: translations.labels.totalNoOfStudent || "Total No. of Students",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: translations.labels.actions || "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title="View Sections"
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaSchool />
        </div>
        <h1 className={styles.headerTitle}>Class List</h1>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        enableSearch={false}
        enableFiltering={false}
      />
    </div>
  );
};

export default ClassList;
